import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import {
  stopSellEditRoles,
  StopSellFilters,
  StopSellModel,
  StopSellRuleTypeOptions
} from '../../../../api/pricing/pricing.types';
import Select from '../../../../components/forms/Select';
import { CarClassModel, DeskModel, ProductModel } from '../../../../api/core/core.types';
import StatusLabel from '../../../../components/forms/StatusLabel';
import { StopSellActionTypes } from '../../types/stopsell.types';
import { getStopSellList, getStopSellsView, updateStopSellFilters } from '../../actions/stopsell.actions';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { DATE_FORMAT, defaultPageSize, PageRequest } from '../../../../api/api.types';
import DatePicker from '../../../../components/forms/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getAllCarClasses } from '../../../core/actions/carClass.actions';
import { formatDateTime } from '../../../../helpers/date.helper';
import ListViewTable from '../../../../components/tables/ListViewTable';
import { UncontrolledTooltip as Tooltip } from 'reactstrap';
import { SupplierBrokerModel } from '../../../../api/admin/admin.types';
import { getSupplierBrokerList } from '../../../admin/actions/supplierbroker.actions';


interface StopSellProps extends RouteComponentProps, WithTranslation {
  getStopSellList: (filters?: StopSellFilters, pageRequest?: PageRequest) => void;
  getAllCarClasses: () => void;
  getDeskList: () => void;
  getBrokerList: () => void;
  getStopSellView: () => void;
  updateFilters: (filters: StopSellFilters) => void;
  stopSells: StopSellModel[] | null;
  carClasses: CarClassModel[];
  desks: DeskModel[];
  products: ProductModel[];
  brokers: SupplierBrokerModel[];
  user: UserModel | null;
  totalPages: number;
  totalElements: number;
  filters: StopSellFilters | null;
}

interface StopSellState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  filter: {
    commentFilter: {
      value: string;
    };
    dateFilter: {
      value: Dayjs | undefined;
    };
    deskFilter: {
      value: [] | { label: string; value: string };
    };
    carClassFilter: {
      value: [] | { label: string; value: string };
    };
    ruleTypeFilter: {
      value: [] | { label: string; value: string };
    };
    brokerFilter: {
      value: [] | { label: string; value: string };
    };
  };
}

const StopSellSortFields = [
  { value: 'validFrom', label: 'validFrom' }
];

class StopSells extends React.Component<StopSellProps, StopSellState> {
  constructor(props: StopSellProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'DESC',
      sortedBy: 'id',
      filtersUpdated: false,
      filter: {
        commentFilter: {
          value: ''
        },
        dateFilter: {
          value: undefined
        },
        deskFilter: {
          value: []
        },
        carClassFilter: {
          value: []
        },
        ruleTypeFilter: {
          value: []
        },
        brokerFilter: {
          value: []
        }
      }
    };
  }

  componentDidMount() {
    this.props.getAllCarClasses();
    this.props.getDeskList();
    this.props.getBrokerList();
    this.props.getStopSellList();
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) this.updateFilterValues();
  }

  getDeskFilterOptions = (desks: string[] | undefined) => {
    const selectedDeskOptions: { value: string; label: string }[] = [];
    if (desks?.length) {
      desks.forEach((sd) => {
        const desk = this.props.desks.find((d) => d.code === sd);
        if (desk) {
          selectedDeskOptions.push({ value: desk.code, label: desk.name });
        }
      });
    }

    return selectedDeskOptions;
  };

  getCarClassFilterOptions = (cars: string[] | undefined) => {
    const selectedCarClassOptions: { value: string; label: string }[] = [];
    if (cars?.length) {
      cars.forEach((sc) => {
        selectedCarClassOptions.push({ value: sc, label: sc });
      });
    }

    return selectedCarClassOptions;
  };

  getBrokerFilterOptions = (brokers: string[] | undefined) => {
    const selectedBrokerOptions: { value: string; label: string }[] = [];
    if (brokers?.length) {
      brokers.forEach((sb) => {
        const broker = this.props.brokers.find((b) => b.id === Number(sb));
        selectedBrokerOptions.push({ value: broker ? String(broker.id) : sb, label: broker ? broker.name : sb });
      });
    }

    return selectedBrokerOptions;
  };

  updateFilterValues = () => {
    this.setState({ filtersUpdated: true });
    const filters = this.props.filters;
    if (filters) {
      const desks = filters.desks ? filters.desks.split(',') : undefined;
      const cars = filters.carClasses ? filters.carClasses.split(',') : undefined;
      const brokers = filters.brokers ? filters.brokers.split(',') : undefined;

      let selectedTypeOption: { value: string; label: string } | [] = [];
      if (filters.ruleType) {
        selectedTypeOption = StopSellRuleTypeOptions.find((opt) => opt.value === filters.ruleType) as {
          value: string;
          label: string
        };
      }

      this.setState({
        selectedPageNumber: filters.pageNumber ? filters.pageNumber : 1,
        filtersUpdated: true,
        filter: {
          commentFilter: {
            value: filters.comment ? filters.comment : ''
          },
          dateFilter: {
            value: filters.date ? dayjs(filters.date) : undefined
          },
          deskFilter: {
            value: this.getDeskFilterOptions(desks)[ 0 ]
          },
          carClassFilter: {
            value: this.getCarClassFilterOptions(cars)[ 0 ]
          },
          brokerFilter: {
            value: this.getBrokerFilterOptions(brokers)[ 0 ]
          },
          ruleTypeFilter: {
            value: selectedTypeOption
          }
        }
      }, () => {
        const filtersapl = this.composeFilters();
        this.props.getStopSellList(filtersapl);
      });
    }
  };

  clearFilters = async () => {
    await this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        commentFilter: {
          value: ''
        },
        dateFilter: {
          value: undefined
        },
        deskFilter: {
          value: []
        },
        carClassFilter: {
          value: []
        },
        brokerFilter: {
          value: []
        },
        ruleTypeFilter: {
          value: []
        }
      }
    });

    const filters = this.composeFilters();
    this.props.updateFilters(filters);
    this.props.getStopSellList();
  };

  validFromCell = (props: any) => {
    if (props.row.original.validFrom.length > 16) {
      return (
        <div className="numeric-content">
          {`${formatDateTime(props.row.original.validFrom)} - ${formatDateTime(props.row.original.validTo)}`}
        </div>
      );
    }

    return <div className="numeric-content">{`${(props.row.original.validFrom).replace(/T/, ' ')}`}</div>;
  };

  onRowClick = (rowInfo: any) => this.props.history.push(`/rates/stopsells/${rowInfo.original.id}/view`);

  carClassCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.stopSellCarClasses.length > 0) {
      const carList: JSX.Element[] = [];
      row.original.stopSellCarClasses.forEach((carClass: any) => {
        const carObj = this.props.carClasses.find((c) => c === carClass.acrissCode);
        carList.push(
          <div
            key={carClass.acrissCode}
            className="flex-50 px-1"
            style={{ maxWidth: '60px' }}
          >
            {carObj ? carObj : carClass.acrissCode}
          </div>
        );
      });
      if (row.original.stopSellCarClasses.length === 1) {
        const carObj = this.props.carClasses.find((d) => d === row.original.stopSellCarClasses[ 0 ].acrissCode);
        return carObj || row.original.stopSellCarClasses[ 0 ].acrissCode;
      }
      return (
        <>
          <div
            className="btn btn-link font-normal p-0"
            id={`car-classes-tooltip-${row.original.id}`}
          >
            {t('v.car_classes', { count: row.original.stopSellCarClasses.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`car-classes-tooltip-${row.original.id}`}
            autohide={false}
          >
            {carList}
          </Tooltip></>
      );
    }
    return t('v.all_car_classes');
  };

  deskCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.stopSellDesks.length > 0) {
      const deskList: JSX.Element[] = [];
      row.original.stopSellDesks.forEach((desk: any) => {
        const deskObj = this.props.desks.find((d) => d.code === desk.deskCode);
        deskList.push(<div key={desk.deskCode}>{deskObj ? deskObj.name : desk.deskCode}</div>);
      });

      if (row.original.stopSellDesks.length === 1) {
        const deskObj = this.props.desks.find((d) => d.code === row.original.stopSellDesks[ 0 ].deskCode);
        return deskObj && deskObj.name || row.original.stopSellDesks[ 0 ].deskCode;
      }
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`desks-tooltip-${row.original.id}`}
          >
            {t('v.desks', { count: row.original.stopSellDesks.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`desks-tooltip-${row.original.id}`}
            autohide={false}
          >
            {deskList}
          </Tooltip>
        </div>
      );
    }
    return t('v.all_desks');
  };

  productCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.stopSellProducts.length > 0) {
      const productList: JSX.Element[] = [];
      row.original.stopSellProducts.forEach((product: any) => {
        const productObj = this.props.products.find((c) => c.id === product.productId);
        productList.push(
          <div
            key={product.productId}
            className="flex-50 px-1"
            style={{ maxWidth: '60px' }}
          >
            {productObj ? productObj.name : product.productId}
          </div>
        );
      });
      if (row.original.stopSellProducts.length === 1) {
        const productObj = this.props.products.find((d) => d.id === row.original.stopSellProducts[ 0 ].productId);
        return productObj && productObj.name || row.original.stopSellProducts[ 0 ].productId;
      }
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`product-tooltip-${row.original.id}`}
          >
            {t('v.stop_sells.products', { count: row.original.stopSellProducts.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`product-tooltip-${row.original.id}`}
            autohide={false}
          >
            {productList}
          </Tooltip>
        </div>
      );
    }
    return t('v.stop_sells.all_products');
  };

  brokerCell = ({ row }: { row: any }) => {
    const { t } = this.props;

    if (row.original.stopSellBrokers.length === 1) {
      const brokerObj = this.props.brokers.find((d) => d.id === row.original.stopSellBrokers[ 0 ].brokerId);
      return brokerObj && brokerObj.id || row.original.stopSellBrokers[ 0 ].brokerId;
    }
    if (row.original.stopSellBrokers.length > 0) {
      const brokerList: JSX.Element[] = [];
      row.original.stopSellBrokers.forEach((broker: any) => {
        const brokerObj = this.props.brokers.find((c) => c.id === broker.brokerId);
        brokerList.push(
          <div
            key={broker.brokerId}
            className="flex-50 px-1"
          >
            {brokerObj ? brokerObj.name : broker.brokerId}
          </div>
        );
      });
      return (
        <div>
          <div
            className="btn btn-link font-normal p-0"
            id={`broker-tooltip-${row.original.id}`}
          >
            {t('v.stop_sells.brokers', { count: row.original.stopSellBrokers.length })}
          </div>
          <Tooltip
            modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
            placement="top"
            target={`broker-tooltip-${row.original.id}`}
            autohide={false}
          >
            {brokerList}
          </Tooltip>
        </div>
      );
    }
    return t('v.stop_sells.all_brokers');
  };

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  ruleTypeCell = (props: any) => {
    const obj = StopSellRuleTypeOptions.find((select) => select.value === props);
    if (obj) {
      return obj.label;
    }

    return '';
  };


  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeFilters();
    this.props.getStopSellList(filters);
    this.props.updateFilters(filters);
  };

  composeFilters = () => {
    const date = this.state.filter.dateFilter.value;

    const deskFilterValue: any = this.state.filter.deskFilter.value;
    const carClassFilterValue: any = this.state.filter.carClassFilter.value;
    const brokerFilterValue: any = this.state.filter.brokerFilter.value;
    const typeFilterValue: any = this.state.filter.ruleTypeFilter.value;


    const filters: StopSellFilters = {
      comment: this.state.filter.commentFilter.value,
      date: date ? date.format(DATE_FORMAT).concat('T08:00:00.0000') : undefined,
      desks: deskFilterValue ? deskFilterValue.value : undefined,
      carClasses: carClassFilterValue ? carClassFilterValue.value : undefined,
      brokers: brokerFilterValue ? brokerFilterValue.value : undefined,
      pageNumber: this.state.selectedPageNumber - 1,
      ruleType: typeFilterValue ? typeFilterValue.value : undefined
    };

    return filters;
  };


  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  handleUpdateResults = () => {
    const filters = this.composeFilters();
    this.props.getStopSellList(filters);
    this.props.updateFilters(filters);
  };

  dropdownChange = (newValue: any, field: string) => {
    this.setState({
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      this.handleUpdateResults();
    });
  };

  onDateChange = (date?: Dayjs) => {
    this.setState({
      filter: {
        ...this.state.filter,
        dateFilter: {
          value: date
        }
      }
    });
  };

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.stop_sells.period'),
        accessorKey: 'validFrom', cell: (info: any) => this.validFromCell(info),
        meta: { align: 'right', width: 250, numeric: true }
      },
      {
        header: t('ct.stop_sells.car_classes'),
        accessorKey: 'stopSellCarClasses', id: 'carClasses',
        cell: ({ row }: { row: any }) => this.carClassCell({ row })
      },
      {
        header: t('ct.stop_sells.desks'),
        accessorKey: 'stopSellDesks',
        cell: ({ row }: { row: any }) => this.deskCell({ row })
      },
      {
        header: t('ct.stop_sells.products'),
        accessorKey: 'stopSellProducts',
        cell: ({ row }: { row: any }) => this.productCell({ row })
      },
      {
        header: t('ct.stop_sells.brokers'),
        accessorKey: 'stopSellBrokers',
        cell: ({ row }: { row: any }) => this.brokerCell({ row })
      },
      {
        header: t('ct.stop_sells.rule'),
        accessorKey: 'ruleType',
        cell: (info: any) => this.ruleTypeCell(info.getValue())
      },
      {
        header: t('ct.stop_sells.status'),
        accessorKey: 'statusType',
        cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    console.log(columnName);
    const sortFieldName = StopSellSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: sortFieldName.label });
    return (
      this.props.getStopSellList(this.composeFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortDirection !== undefined) {
      const pageRequestFix = {
        page: pageRequest.page,
        sortDirection: pageRequest.sortDirection,
        sortField: StopSellSortFields.find((column) => column.label === pageRequest.sortField)?.label ?? 'id'
      };
      this.props.getStopSellList(this.composeFilters(), pageRequestFix);

      this.setState({
        selectedPageNumber: pageRequestFix.page + 1,
        sortOrder: pageRequestFix.sortDirection ?? 'ASC',
        sortedBy: StopSellSortFields.find((column) => column.label === pageRequest.sortField)?.label ?? 'id'
      });
    }
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>Stop Sells</h1>
            {this.props.user && userCanAccessComponent(this.props.user, stopSellEditRoles) && (
              <Link
                to={{
                  pathname: '/rates/stopsells/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex ml-1 row col-10 flex-wrap filters-container">
              <input
                value={this.state.filter.commentFilter.value}
                type="text"
                name="commentFilter"
                className="form-control col filter-select margins filter-item-margin"
                placeholder={t('ph.filter.stop_sells.by_comment')}
                onChange={this.changeHandler}
              />
              <div className="col filter-select filter-item-margin">
                <DatePicker
                  isEdit
                  isFilter
                  date={this.state.filter.dateFilter.value}
                  onChange={(val) => this.onDateChange(val)}
                />
              </div>
              <Select
                value={this.state.filter.carClassFilter.value}
                className="mr-1"
                equalWidthElement
                marginTop
                placeholder={t('ph.filter.stop_sells.by_car_class')}
                // eslint-disable-next-line no-nested-ternary
                options={this.props.carClasses.sort((a, b) =>
                  ((a.acrissCode < b.acrissCode) ? -1 : (a.acrissCode > b.acrissCode) ? 1 : 0)).map((m) => ({
                  label: m.acrissCode,
                  value: m.acrissCode
                }))}
                onChange={(val) => this.dropdownChange(val, 'carClassFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="car class"
              />
              <div className="margins-form-select ml-0">
                <Select
                  value={this.state.filter.deskFilter.value}
                  marginTop
                  placeholder={t('ph.filter.stop_sells.by_location')}
                  elWidth={250}
                  // eslint-disable-next-line no-nested-ternary
                  options={this.props.desks.sort((a, b) => ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0)).map((d) => ({
                    label: d.name,
                    value: d.code
                  }))}
                  onChange={(val) => this.dropdownChange(val, 'deskFilter')}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="location"
                />
              </div>
              <div className="margins-form-select ml-0">
                <Select
                  value={this.state.filter.ruleTypeFilter.value || undefined}
                  marginTop={true}
                  elWidth={250}
                  placeholder="Filter by rule type"
                  // eslint-disable-next-line no-nested-ternary
                  options={StopSellRuleTypeOptions.sort(
                    (a, b) =>
                      ((a.label < b.label) ? -1 : (a.label > b.label) ? 1 : 0))}
                  onChange={(val) =>
                    this.dropdownChange(val, 'ruleTypeFilter')}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="ruleType"
                />
              </div>
              <Select
                value={this.state.filter.brokerFilter.value}
                equalWidthElement
                className="mr-1"
                marginTop
                placeholder={t('ph.filter.stop_sells.by_broker')}
                // eslint-disable-next-line no-nested-ternary
                options={this.props.brokers.sort((a, b) => ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0)).map((d) => ({
                  label: d.name,
                  value: String(d.id)
                }))}
                onChange={(val) => this.dropdownChange(val, 'brokerFilter')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="brokers"
              />
            </div>
            <div className="col2 pl-1">
              <button type="button" className="btn btn-text-only mr-2" onClick={() => this.clearFilters()}>
                {t('b.clear_filter')}
              </button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}
              </button>
            </div>
          </form>
          {this.props.stopSells
            ? (
              <div className="table-list">
                <ListViewTable
                  data={this.props.stopSells}
                  columns={this.defineColumns()}
                  getTrProps={this.onRowClick}
                  sortBy={this.sortBy}
                  sortOrder={this.state.sortOrder}
                  sortedBy={StopSellSortFields.find(({ value, label }) => this.state.sortedBy === label)?.value}
                  paginationEnabled={true}
                  totalPages={this.props.totalPages}
                  totalElements={this.props.totalElements}
                  selectedPageNumber={this.state.selectedPageNumber}
                  getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
                />
              </div>
            ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  stopSells: state.rates.stopSell.stopSells,
  carClasses: state.core.carClasses.acrissCodes,
  desks: state.setup.desks.desks,
  products: state.setup.products.products,
  brokers: state.admin.supplierBrokers.supplierBrokers,
  user: state.authentication.user,
  totalPages: state.rates.stopSell.totalPages,
  totalElements: state.rates.stopSell.totalElements,
  filters: state.rates.stopSell.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, StopSellActionTypes>) => ({
  getStopSellList: (filters?: StopSellFilters, pageRequest?: PageRequest) => dispatch(getStopSellList(filters, pageRequest)),
  getDeskList: () => dispatch(getDeskList()),
  getAllCarClasses: () => dispatch(getAllCarClasses()),
  getBrokerList: () => dispatch(getSupplierBrokerList()),
  getStopSellView: () => dispatch(getStopSellsView()),
  updateFilters: (filters: StopSellFilters) => dispatch(updateStopSellFilters(filters))
});
export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(StopSells));
